import React, { useContext, useRef } from 'react';
import { FormContext } from '../../context/EquipmentFormContext';
import { LuFileText, LuX } from 'react-icons/lu';
import { FiCalendar, FiUploadCloud } from 'react-icons/fi';

export default function Step8() {
  const { formData, updateField } = useContext(FormContext);

  const fileInputRef = useRef(null);

  // ============================================================
  // HANDLE DOCUMENT SELECT
  // ============================================================

  const handleDocumentSelect = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // ----------------------------------------------------------
    // Allowed document types
    // ----------------------------------------------------------

    const allowedTypes = [
      'application/pdf',

      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',

      'text/plain',

      'image/jpeg',
      'image/png',
      'image/webp',
    ];

    if (!allowedTypes.includes(file.type)) {
      alert(
        'Please select a PDF, Word, Excel, PowerPoint, TXT, JPG, PNG or WEBP file.'
      );

      e.target.value = '';
      return;
    }

    // ----------------------------------------------------------
    // Maximum file size: 10MB
    // ----------------------------------------------------------

    const maxSize = 10 * 1024 * 1024;

    if (file.size > maxSize) {
      alert('Please select a document smaller than 10MB.');

      e.target.value = '';
      return;
    }

    // ----------------------------------------------------------
    // Store file in context
    // ----------------------------------------------------------

    updateField('documentFile', file);

    updateField(
      'documentName',
      file.name
    );

    updateField(
      'documentType',
      file.type
    );

    // We are storing the actual File in context.
    // documentUrl can remain empty until the backend uploads it.
    updateField(
      'documentUrl',
      ''
    );
  };

  // ============================================================
  // OPEN FILE PICKER
  // ============================================================

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // ============================================================
  // REMOVE DOCUMENT
  // ============================================================

  const handleRemoveDocument = () => {
    updateField(
      'documentFile',
      null
    );

    updateField(
      'documentName',
      ''
    );

    updateField(
      'documentType',
      ''
    );

    updateField(
      'documentUrl',
      ''
    );

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">

      {/* ========================================================
          HEADER
      ======================================================== */}

      <div className="flex items-start space-x-4 mb-6">

        <div className="p-2 bg-blue-600 rounded-lg text-white">
          <LuFileText className="w-5 h-5 stroke-[2.5]" />
        </div>

        <div>

          <div className="flex items-center space-x-2">

            <span className="text-xs font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              8
            </span>

            <h2 className="text-base font-bold text-indigo-950">
              Warranty & Documents
            </h2>

          </div>

          <p className="text-xs text-slate-500 mt-0.5">
            Add warranty details and upload certificates, manuals and other documents.
          </p>

        </div>

      </div>

      {/* ========================================================
          FORM
      ======================================================== */}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">

        {/* ======================================================
            WARRANTY
        ====================================================== */}

        <div className="md:col-span-2">

          <label className="block text-xs font-semibold text-slate-700 mb-2">
            Warranty
          </label>

          <div className="flex items-center space-x-4 h-9">

            <label className="flex items-center text-sm font-semibold text-slate-800 cursor-pointer">

              <input
                type="radio"
                name="warranty_active"
                checked={
                  formData.warrantyActive === 'Yes'
                }
                onChange={() =>
                  updateField(
                    'warrantyActive',
                    'Yes'
                  )
                }
                className="w-4 h-4 text-blue-600 border-slate-300"
              />

              <span className="ml-2">
                Yes
              </span>

            </label>

            <label className="flex items-center text-sm font-semibold text-slate-800 cursor-pointer">

              <input
                type="radio"
                name="warranty_active"
                checked={
                  formData.warrantyActive === 'No'
                }
                onChange={() =>
                  updateField(
                    'warrantyActive',
                    'No'
                  )
                }
                className="w-4 h-4 text-blue-600 border-slate-300"
              />

              <span className="ml-2">
                No
              </span>

            </label>

          </div>

        </div>

        {/* ======================================================
            WARRANTY EXPIRY
        ====================================================== */}

        <div className="md:col-span-2">

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Warranty Expiry Date
          </label>

          <div className="relative">

            <input
              type="text"
              value={formData.warrantyExpiryDate}
              onChange={(e) =>
                updateField(
                  'warrantyExpiryDate',
                  e.target.value
                )
              }
              placeholder="DD MMM YYYY"
              className="w-full text-sm border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />

            <FiCalendar className="w-4 h-4 text-slate-400 absolute left-3 top-3" />

          </div>

        </div>

        {/* ======================================================
            WARRANTY PROVIDER
        ====================================================== */}

        <div className="md:col-span-3">

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Warranty Provider
          </label>

          <input
            type="text"
            value={formData.warrantyProvider}
            onChange={(e) =>
              updateField(
                'warrantyProvider',
                e.target.value
              )
            }
            placeholder="e.g. Manufacturer"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />

        </div>

        {/* ======================================================
            WARRANTY REFERENCE
        ====================================================== */}

        <div className="md:col-span-3">

          <label className="block text-xs font-semibold text-slate-700 mb-1">
            Warranty Reference
          </label>

          <input
            type="text"
            value={formData.warrantyReference}
            onChange={(e) =>
              updateField(
                'warrantyReference',
                e.target.value
              )
            }
            placeholder="e.g. W-12345"
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />

        </div>

        {/* ======================================================
            HIDDEN FILE INPUT
        ====================================================== */}

        <input
          ref={fileInputRef}
          type="file"
          accept="
            .pdf,
            .doc,
            .docx,
            .xls,
            .xlsx,
            .ppt,
            .pptx,
            .txt,
            .jpg,
            .jpeg,
            .png,
            .webp
          "
          onChange={handleDocumentSelect}
          className="hidden"
        />

        {/* ======================================================
            UPLOAD BUTTON
        ====================================================== */}

        <div className="md:col-span-2">

          <button
            type="button"
            onClick={handleUploadClick}
            className="w-full flex items-center justify-center h-[38px] px-3 border border-blue-200 text-blue-700 font-semibold text-xs rounded-lg hover:bg-blue-50 transition-colors"
          >

            <FiUploadCloud className="w-4 h-4 mr-2" />

            {formData.documentFile
              ? 'Change Document'
              : 'Upload Document'}

          </button>

        </div>

      </div>

      {/* ========================================================
          SELECTED DOCUMENT
      ======================================================== */}

      {formData.documentFile && (
        <div className="mt-4">

          <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">

            <div className="flex items-center min-w-0">

              <div className="p-2 bg-blue-100 text-blue-600 rounded-lg mr-3 shrink-0">
                <LuFileText className="w-5 h-5" />
              </div>

              <div className="min-w-0">

                <p className="text-xs font-semibold text-slate-800 truncate">
                  {formData.documentName}
                </p>

                <p className="text-[10px] text-slate-400 mt-0.5">
                  {formData.documentType || 'Document'}
                  {' • '}
                  {(formData.documentFile.size / 1024 / 1024).toFixed(2)} MB
                </p>

              </div>

            </div>

            <button
              type="button"
              onClick={handleRemoveDocument}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
              title="Remove document"
            >

              <LuX className="w-4 h-4" />

            </button>

          </div>

        </div>
      )}

    </div>
  );
}